import React, { useCallback, useState } from 'react';

import { Popover } from '@mui/material';
import {
	DateCalendar,
	DateCalendarProps,
	DateCalendarSlotProps,
	DateCalendarSlots,
} from '@mui/x-date-pickers';
import { DateTime } from 'luxon';

import type { TRange } from '@common/helpers/useRangeDateQuery';

import DateRangePickerDayContainer from '../PickersDay/container';

export interface CalendarPopoverProps extends DateCalendarProps<any> {
	anchorEl: HTMLElement | null;
	isCalendarOpen: boolean;
	onClose: () => void;
	value: TRange;
	onChange: (value: TRange) => void;
}

function DateRangePickerPopover({
	anchorEl,
	isCalendarOpen,
	onClose,
	value,
	onChange,
	...props
}: CalendarPopoverProps) {
	const [isSelectingEndDate, setIsSelectingEndDate] = useState<boolean>(false);
	const [hoveredDate, setHoveredDate] = useState<DateTime | null>(null);

	const handleDateChange = useCallback(
		(newDate: DateTime | null) => {
			if (!newDate) return;

			let newRange: TRange = [value[0], value[1]];

			// If the start date is not selected yet or we're starting a new range
			if (!value[0] || (!isSelectingEndDate && value[0] && value[1])) {
				newRange = [newDate, value[1]];

				// If the end date exists, but the new date is greater than the end date
				if (value[1] && newDate > value[1]) {
					newRange = [newDate, newDate];
				}

				// Switch to selecting the end date
				setIsSelectingEndDate(true);
			} else if (isSelectingEndDate) {
				// If we are selecting the end date
				if (newDate < value[0]!) {
					// If the new date is earlier than the start date, start a new range
					newRange = [newDate, newDate];
					setIsSelectingEndDate(true); // Stay in the "selecting end date" state
				} else {
					// If the new date is later than the start date, update the end date
					newRange = [value[0], newDate];
					setIsSelectingEndDate(false); // Finish the selection
				}
			}

			// Update the range value
			onChange(newRange);
		},
		[value, isSelectingEndDate],
	);

	return (
		<Popover
			open={isCalendarOpen}
			anchorEl={anchorEl}
			onClose={onClose}
			slotProps={{ paper: { sx: { zIndex: 0 } } }}
		>
			<DateCalendar
				value={value[0] || value[1]}
				onChange={handleDateChange}
				slots={{
					day: DateRangePickerDayContainer as DateCalendarSlots<any>['day'],
				}}
				slotProps={{
					day: {
						hoveredDate,
						setHoveredDate,
						tempDateRange: value,
						isSelectingEndDate,
					} as DateCalendarSlotProps<any>['day'],
				}}
				{...props}
			/>
		</Popover>
	);
}

export default DateRangePickerPopover;
